import { useEffect, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiMagnify,
  mdiCloseCircleOutline,
} from "@mdi/js";
import "../inward-styles.css";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import InwardOrdersTable from "./InwardOrdersTable";
import LoadingWidget from "../../../../context_providers/modal/loader";
import PaginationWarehouse from "./PaginationWarehouse";
import {
  useWarehouseInward,
  WarehouseInwardProvider,
} from "../contexts/InwardContext";
import AwaitingReturnsCardList from "./AwaitingReturnsCardList";
import WarehouseSearchFieldByNameID from "../common-components/WarehouseSearchFieldByNameID";
import { useWarehouseViewOnly } from "../hooks/useWarehouseViewOnly";
import MultiSelectDropdown from "../common-components/MultiSelectDropdown";
import ItemSearchTagInput from "../common-components/ItemSearchTagInput";
import { mdiEmailOutline } from "@mdi/js";
import { exportInwardByEmail } from "../../../../erp/accounts/vouchers/purchase/service/inward_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import ModalDialog from "../../../new_components/common/modal";

const InwardPage = () => {
  return (
    <WarehouseInwardProvider storageKey="warehouse:inward:filters:v2">
      <InwardListScreen />
    </WarehouseInwardProvider>
  );
};

export default InwardPage;

const InwardListScreen = () => {
  const { hasRoleTitle } = useAuth();
  const isWarehouseViewOnly = useWarehouseViewOnly();
  const {
    inwardList,
    awaitingReturns,
    isLoading,
    isRefreshing,
    statusCount,
    filters,
    setFilters,
    page,
    setPage,
    pagination,
  } = useWarehouseInward();

  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);
  const [canUpdateInward, setCanUpdateInward] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [cascadeHistory, setCascadeHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPO, setExportPO] = useState(true);
  const [exportST, setExportST] = useState(true);
  const { showToast } = useToast();

  const openExportModal = () => {
    setExportPO(true);
    setExportST(true);
    setShowExportModal(true);
  };

  const handleExport = async () => {
    if (exporting) return;
    const selected = [exportPO ? "PO" : null, exportST ? "ST" : null].filter(
      Boolean,
    ) as string[];
    if (!selected.length) return;
    setExporting(true);
    try {
      const res = await exportInwardByEmail({
        search: filters.search,
        status: filters.status?.length ? filters.status.join(",") : undefined,
        type: filters.type?.length ? filters.type.join(",") : undefined,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fromWarehouse: filters.fromWarehouse,
        toWarehouse: filters.toWarehouse,
        itemSearch: filters.itemSearch,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        docTypes: selected.join(","),
      });
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "Inward export will be emailed to you shortly.",
        });
        setShowExportModal(false);
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to schedule export",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to schedule export",
      });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const isFinanceTeam = hasRoleTitle({
      roleTitle: "warehouse module finance",
    });

    setCanUpdateInward(!isFinanceTeam && !isWarehouseViewOnly);

    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setShowFilters(!mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hasRoleTitle, isWarehouseViewOnly]);

  return (
    <div
      id="warehouse-inward-dashboard"
      className="wh-mobile-shrink flex flex-col gap-2 min-h-screen"
    >
      <div className="p-4 md:p-6 animate-fade-in">
        <div className="flex flex-wrap justify-between items-end gap-3">
          <h1 className="text-3xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Inward Management
          </h1>
          <button
            id="warehouse-inward-export-btn"
            onClick={openExportModal}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Icon path={mdiEmailOutline} size={0.8} />
            Export by Email
          </button>
        </div>

        <ModalDialog
          show={showExportModal}
          title="Export by Email"
          onClose={() => {
            if (!exporting) setShowExportModal(false);
          }}
        >
          <div className="flex flex-col gap-4 p-2">
            <p className="text-sm text-muted-foreground">
              Select which exports to generate. Each selected type is emailed to
              you as its own mail with a CSV download link.
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportPO}
                disabled={exporting}
                onChange={(e) => setExportPO(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">PO Export</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={exportST}
                disabled={exporting}
                onChange={(e) => setExportST(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">ST Export</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exporting || (!exportPO && !exportST)}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Icon path={mdiEmailOutline} size={0.8} />
                {exporting ? "Scheduling…" : "Export"}
              </button>
            </div>
          </div>
        </ModalDialog>

        <div className="space-y-6 mt-4">
          <div className="flex gap-4 overflow-x-auto md:grid md:grid-cols-6 py-2">
            {/* Pending */}
            <div
              id="warehouse-inward-stat-pending"
              className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-warning bg-white shadow-sm"
            >
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{statusCount.pending}</p>
              </div>
            </div>

            {/* Partial */}
            <div
              id="warehouse-inward-stat-partial"
              className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-info bg-white shadow-sm"
            >
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Partial</p>
                <p className="text-2xl font-bold">{statusCount.partial}</p>
              </div>
            </div>

            {/* Received */}
            <div
              id="warehouse-inward-stat-received"
              className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-success bg-white shadow-sm"
            >
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Received</p>
                <p className="text-2xl font-bold">{statusCount.received}</p>
              </div>
            </div>

            {/* Disputed */}
            <div
              id="warehouse-inward-stat-disputed"
              className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-destructive bg-white shadow-sm"
            >
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Disputed</p>
                <p className="text-2xl font-bold">{statusCount.disputed}</p>
              </div>
            </div>

            {/* Returnable */}
            <div
              id="warehouse-inward-stat-returnable"
              className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-primary bg-white shadow-sm"
            >
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Returnable</p>
                <p className="text-2xl font-bold">{statusCount.returnable}</p>
              </div>
            </div>

            {/* Replacements */}
            <div
              id="warehouse-inward-stat-replacements"
              className="min-w-[160px] md:min-w-0 rounded-lg border-l-4 border-l-info bg-white shadow-sm"
            >
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Replacements</p>
                <p className="text-2xl font-bold">{statusCount.replacements}</p>
              </div>
            </div>
          </div>

          <AwaitingReturnsCardList
            awaitingReturns={awaitingReturns}
            updatesAllowed={canUpdateInward}
          />

          <div className="flex items-center justify-between">
            <div
              id="warehouse-inward-filters-toggle"
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setShowFilters((p) => !p)}
            >
              <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                Filters
                <Icon
                  path={showFilters ? mdiChevronUp : mdiChevronDown}
                  size={0.8}
                />
              </div>
            </div>

            {(filters.search ||
              filters.status?.length ||
              filters.type?.length ||
              filters.fromDate ||
              filters.toDate ||
              filters.fromWarehouse ||
              filters.toWarehouse ||
              filters.itemSearch) && (
              <button
                id="warehouse-inward-clear-filters-btn"
                onClick={() =>
                  setFilters({
                    search: "",
                    status: [],
                    type: [],
                    fromDate: "",
                    toDate: "",
                    fromWarehouse: "",
                    toWarehouse: "",
                    itemSearch: "",
                  })
                }
                className="text-sm font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"
              >
                <Icon path={mdiCloseCircleOutline} size={0.6} />
                Clear All Filters
              </button>
            )}
          </div>

          {showFilters && (
            <div className="rounded-lg border border-gray-300 shadow-sm bg-white">
              {/* ROW 1 */}
              <div className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1 flex items-center gap-2">
                    <div className="relative flex-1">
                      <Icon
                        path={mdiMagnify}
                        className="absolute left-3 top-1/3 -translate-y-1/2 h-5 w-5 text-muted-foreground"
                      />
                      <input
                        id="warehouse-inward-filter-search"
                        className="flex h-10 w-full rounded-md border bg-background px-3 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Search by Order ID or Vendor..."
                        value={filters.search || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            search: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {filters.search && (
                      <button
                        id="warehouse-inward-filter-search-clear"
                        onClick={() =>
                          setFilters((prev) => ({ ...prev, search: "" }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear Search"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  <MultiSelectDropdown
                    id="warehouse-inward-filter-status"
                    placeholder="All Status"
                    values={filters.status || []}
                    onChange={(next) =>
                      setFilters((prev) => ({ ...prev, status: next }))
                    }
                    options={[
                      { value: "pending", label: "Pending" },
                      { value: "partial", label: "Partial" },
                      { value: "received", label: "Received" },
                      { value: "disputed", label: "Disputed" },
                    ]}
                  />

                  <MultiSelectDropdown
                    id="warehouse-inward-filter-type"
                    placeholder="All Types"
                    values={filters.type || []}
                    onChange={(next) =>
                      setFilters((prev) => ({ ...prev, type: next }))
                    }
                    options={[
                      { value: "PO", label: "Purchase Order" },
                      { value: "ST", label: "Stock Transfer" },
                    ]}
                  />
                </div>
              </div>

              {/* ROW 2 */}
              <div className="p-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500">
                      Expected Date (From)
                    </label>

                    <div className="flex items-center gap-2">
                      <input
                        id="warehouse-inward-filter-from-date"
                        type="date"
                        value={filters.fromDate || ""}
                        onChange={(e) =>
                          setFilters((p) => ({
                            ...p,
                            fromDate: e.target.value,
                          }))
                        }
                        className="h-10 w-full rounded-md border px-3 text-sm"
                        placeholder="From Date"
                      />

                      {filters.fromDate && (
                        <button
                          onClick={() =>
                            setFilters((p) => ({ ...p, fromDate: "" }))
                          }
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Icon path={mdiCloseCircleOutline} size={0.7} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* To Date */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500">
                      Expected Date (To)
                    </label>

                    <div className="flex items-center gap-2">
                      <input
                        id="warehouse-inward-filter-to-date"
                        type="date"
                        value={filters.toDate || ""}
                        onChange={(e) =>
                          setFilters((p) => ({ ...p, toDate: e.target.value }))
                        }
                        className="h-10 w-full rounded-md border px-3 text-sm"
                        placeholder="To Date"
                      />

                      {filters.toDate && (
                        <button
                          onClick={() =>
                            setFilters((p) => ({ ...p, toDate: "" }))
                          }
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Icon path={mdiCloseCircleOutline} size={0.7} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* From Warehouse */}
                  <WarehouseSearchFieldByNameID
                    id="warehouse-inward-filter-from-warehouse"
                    placeholder="From Warehouse (Name)"
                    value={filters.fromWarehouse || ""}
                    onChange={(val) =>
                      setFilters((p) => ({
                        ...p,
                        fromWarehouse: val,
                      }))
                    }
                    onClear={() =>
                      setFilters((p) => ({ ...p, fromWarehouse: "" }))
                    }
                  />

                  {/* To Warehouse */}
                  <WarehouseSearchFieldByNameID
                    id="warehouse-inward-filter-to-warehouse"
                    placeholder="To Warehouse (Name)"
                    value={filters.toWarehouse || ""}
                    onChange={(val) =>
                      setFilters((p) => ({
                        ...p,
                        toWarehouse: val,
                      }))
                    }
                    onClear={() =>
                      setFilters((p) => ({ ...p, toWarehouse: "" }))
                    }
                  />
                </div>
              </div>

              {/* ROW 3 */}
              <div className="p-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  {/* 50% */}
                  <div className="md:col-span-2 flex flex-col gap-1">
                    <label className="text-xs text-gray-500">
                      Item Code / Name
                    </label>

                    <ItemSearchTagInput
                      id="warehouse-inward-filter-item-search"
                      value={filters.itemSearch || ""}
                      onChange={(next) =>
                        setFilters((p) => ({ ...p, itemSearch: next }))
                      }
                    />
                  </div>

                  <div className="inline-flex items-center gap-2 h-10 select-none">
                    <input
                      id="warehouse-inward-filter-expand-items"
                      type="checkbox"
                      checked={cascade}
                      onChange={(e) => setCascade(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />

                    <span className="text-sm text-gray-700">Expand items</span>
                  </div>

                  <div className="inline-flex items-center gap-2 h-10 select-none">
                    <input
                      id="warehouse-inward-filter-expand-history"
                      type="checkbox"
                      checked={cascadeHistory}
                      onChange={(e) => setCascadeHistory(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />

                    <span className="text-sm text-gray-700">
                      Expand history
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {inwardList && inwardList.length > 0 && (
            <>
              <InwardOrdersTable
                updatesAllowed={canUpdateInward}
                cascade={cascade}
                cascadeHistory={cascadeHistory}
              />

              <PaginationWarehouse
                id="warehouse-inward-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => {
                  if (newPage >= 1 && newPage <= pagination.totalPages) {
                    setPage(newPage);
                  }
                }}
              />
            </>
          )}

          {inwardList && inwardList.length === 0 && !isLoading && (
            <p className="text-center text-muted-foreground mt-10">
              No inward orders found.
            </p>
          )}
        </div>
      </div>

      {(isLoading || isRefreshing) && <LoadingWidget />}
    </div>
  );
};
